import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, MessageCircle, Sparkles, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { catalog } from '../data/catalog';
import { jsPDF } from 'jspdf';

const LOGO_URL = 'https://customer-assets-4nw71qhi.emergentagent.net/job_flavored-ice-factory/artifacts/iyqyn1tc_IMG_3682.png';

const fetchAsDataURL = async (url) => {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export default function Catalog() {
  const [downloading, setDownloading] = useState(false);

  const openWhatsApp = (flavor) => {
    const text = `Olá, gostaria de solicitar um orçamento para Gelo Saborizado ${flavor} (200g).`;
    window.open(`https://wa.me/5511981919933?text=${encodeURIComponent(text)}`, '_blank');
  };

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();

      // Pre-load all images as data URLs (avoid CORS taint)
      const [logoData, ...imgs] = await Promise.all([
        fetchAsDataURL(LOGO_URL),
        ...catalog.map((f) => fetchAsDataURL(f.image)),
      ]);

      // === COVER PAGE ===
      pdf.setFillColor(10, 22, 40); // #0a1628
      pdf.rect(0, 0, pageW, pageH, 'F');

      // Gold accent bar
      pdf.setFillColor(125, 211, 252); // #7dd3fc
      pdf.rect(0, pageH - 8, pageW, 8, 'F');

      // Logo in a white rounded card (same style as site header)
      const logoProps = pdf.getImageProperties(logoData);
      const logoRatio = Math.min(60 / logoProps.width, 60 / logoProps.height);
      const lw = logoProps.width * logoRatio;
      const lh = logoProps.height * logoRatio;
      // White card background with light-blue border
      const cardW = lw + 20;
      const cardH = lh + 16;
      const cardX = pageW / 2 - cardW / 2;
      const cardY = 60 + (60 - lh) / 2 - 8;
      pdf.setFillColor(255, 255, 255);
      pdf.setDrawColor(125, 211, 252);
      pdf.setLineWidth(0.5);
      pdf.roundedRect(cardX, cardY, cardW, cardH, 5, 5, 'FD');
      pdf.addImage(logoData, 'PNG', pageW / 2 - lw / 2, 60 + (60 - lh) / 2, lw, lh);

      // Title
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(36);
      pdf.text('CATÁLOGO', pageW / 2, 145, { align: 'center' });
      pdf.setTextColor(125, 211, 252);
      pdf.text('DIGITAL', pageW / 2, 160, { align: 'center' });

      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(14);
      pdf.text('Gelos Saborizados 200g', pageW / 2, 185, { align: 'center' });

      // Footer info
      pdf.setFontSize(10);
      pdf.setTextColor(200, 200, 200);
      pdf.text('Império Ice Ltda. | CNPJ: 63.234.127/0001-95', pageW / 2, pageH - 25, { align: 'center' });
      pdf.text('comercial@imperioice.com.br | (11) 98191-9933', pageW / 2, pageH - 18, { align: 'center' });

      // === PRODUCT PAGES: 2 per page ===
      const perPage = 2;
      for (let i = 0; i < catalog.length; i += perPage) {
        pdf.addPage();
        pdf.setFillColor(10, 22, 40);
        pdf.rect(0, 0, pageW, pageH, 'F');

        // Top bar
        pdf.setFillColor(125, 211, 252);
        pdf.rect(0, 0, pageW, 3, 'F');

        // Page title
        pdf.setTextColor(125, 211, 252);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(20);
        pdf.text('Nossos Sabores', pageW / 2, 20, { align: 'center' });

        const chunk = catalog.slice(i, i + perPage);
        chunk.forEach((flavor, idx) => {
          const yBase = 35 + idx * 125;

          // Card background
          pdf.setFillColor(15, 31, 58); // #0f1f3a
          pdf.roundedRect(15, yBase, pageW - 30, 115, 6, 6, 'F');

          // Product image (left) — preserve aspect ratio
          try {
            const props = pdf.getImageProperties(imgs[i + idx]);
            const fmt = (props.fileType || 'PNG').toUpperCase();
            const maxW = 55;
            const maxH = 100;
            const ratio = Math.min(maxW / props.width, maxH / props.height);
            const w = props.width * ratio;
            const h = props.height * ratio;
            const cellCenterX = 22 + maxW / 2;
            const cellCenterY = yBase + 8 + maxH / 2;
            pdf.addImage(imgs[i + idx], fmt, cellCenterX - w / 2, cellCenterY - h / 2, w, h);
          } catch (e) {
            console.warn('img err', e);
          }

          // Text content (right)
          pdf.setTextColor(255, 255, 255);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(22);
          pdf.text(flavor.name, 90, yBase + 25);

          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(11);
          pdf.setTextColor(125, 211, 252);
          pdf.text('Gelo Saborizado • 200g', 90, yBase + 34);

          pdf.setTextColor(200, 200, 200);
          pdf.setFontSize(10);
          const desc = pdf.splitTextToSize(flavor.description, pageW - 110);
          pdf.text(desc, 90, yBase + 46);

          if (flavor.badge) {
            pdf.setFillColor(125, 211, 252);
            pdf.roundedRect(90, yBase + 65, 60, 8, 2, 2, 'F');
            pdf.setTextColor(10, 22, 40);
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'bold');
            pdf.text(flavor.badge, 120, yBase + 70.5, { align: 'center' });
          }

          // Contact CTA
          pdf.setTextColor(255, 255, 255);
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(9);
          pdf.text('Solicite pelo WhatsApp: (11) 98191-9933', 90, yBase + 100);
        });

        // Page number
        pdf.setTextColor(180, 180, 180);
        pdf.setFontSize(9);
        pdf.text(`Página ${Math.floor(i / perPage) + 2}`, pageW / 2, pageH - 8, { align: 'center' });
      }

      // === LAST PAGE — Contact ===
      pdf.addPage();
      pdf.setFillColor(10, 22, 40);
      pdf.rect(0, 0, pageW, pageH, 'F');

      // Logo in white card (same style as site header)
      const logoRatio2 = Math.min(40 / logoProps.width, 40 / logoProps.height);
      const lw2 = logoProps.width * logoRatio2;
      const lh2 = logoProps.height * logoRatio2;
      const card2W = lw2 + 14;
      const card2H = lh2 + 12;
      const card2X = pageW / 2 - card2W / 2;
      const card2Y = 30 + (40 - lh2) / 2 - 6;
      pdf.setFillColor(255, 255, 255);
      pdf.setDrawColor(125, 211, 252);
      pdf.setLineWidth(0.5);
      pdf.roundedRect(card2X, card2Y, card2W, card2H, 4, 4, 'FD');
      pdf.addImage(logoData, 'PNG', pageW / 2 - lw2 / 2, 30 + (40 - lh2) / 2, lw2, lh2);

      pdf.setTextColor(125, 211, 252);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(24);
      pdf.text('Fale Conosco', pageW / 2, 90, { align: 'center' });

      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(12);
      const contactLines = [
        'WhatsApp: (11) 98191-9933',
        'E-mail: comercial@imperioice.com.br',
        'Endereço: Rua Segundo Sargento Ananias Holanda de Oliveira, nº 19 - São Paulo/SP',
        'Site: imperioice.com.br',
        'Instagram: @imperio.ice__',
      ];
      contactLines.forEach((line, i) => pdf.text(line, pageW / 2, 115 + i * 10, { align: 'center' }));

      pdf.setFillColor(125, 211, 252);
      pdf.rect(0, pageH - 15, pageW, 15, 'F');
      pdf.setTextColor(10, 22, 40);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.text('IMPÉRIO ICE LTDA. — QUALIDADE PREMIUM EM GELOS SABORIZADOS', pageW / 2, pageH - 5.5, { align: 'center' });

      pdf.save('Catalogo-Imperio-Ice.pdf');
    } catch (err) {
      console.error(err);
      alert('Erro ao gerar o PDF. Tente novamente.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="catalog-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-[#0a1628]/95 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-[#7dd3fc] transition-colors" data-testid="catalog-back-link">
            <ArrowLeft size={18} />
            <span className="text-sm">Voltar ao site</span>
          </Link>
          <div className="bg-white rounded-xl px-3 py-1.5 shadow-md shadow-[#7dd3fc]/30 border border-[#7dd3fc]/40">
            <img
              src="https://customer-assets-4nw71qhi.emergentagent.net/job_flavored-ice-factory/artifacts/iyqyn1tc_IMG_3682.png"
              alt="Império Ice"
              className="h-9 w-auto object-contain"
            />
          </div>
          <div className="w-24 hidden md:block" />
        </div>
      </div>

      {/* Hero */}
      <section className="relative py-16 md:py-24 px-6 overflow-hidden">
        <div className="absolute top-10 right-10 w-64 h-64 bg-[#7dd3fc]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-80 h-80 bg-[#7dd3fc]/10 rounded-full blur-3xl" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 max-w-4xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-[#7dd3fc]/10 rounded-full mb-6">
            <Sparkles className="text-[#7dd3fc]" size={20} />
            <span className="text-[#7dd3fc] font-semibold">Catálogo Digital</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
            Nossos <span className="text-[#7dd3fc]">Sabores</span>
          </h1>
          <p className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto mb-8">
            Conheça toda a linha de Gelos Saborizados Império Ice — 200g cada, sabor intenso e refrescante.
          </p>

          <motion.button
            onClick={downloadPDF}
            disabled={downloading}
            whileHover={{ scale: downloading ? 1 : 1.05 }}
            whileTap={{ scale: downloading ? 1 : 0.95 }}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#7dd3fc] to-[#bae6fd] text-[#0a1628] font-bold rounded-xl shadow-xl shadow-[#7dd3fc]/30 disabled:opacity-60 disabled:cursor-wait"
            data-testid="catalog-download-pdf-btn"
          >
            <Download size={20} />
            <span>{downloading ? 'Gerando PDF...' : 'Baixar Catálogo em PDF'}</span>
          </motion.button>
        </motion.div>
      </section>

      {/* Bottom CTA */}
      <section className="pb-24 px-6">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-2xl mx-auto p-10 bg-gradient-to-br from-[#7dd3fc]/10 to-transparent rounded-3xl border border-[#7dd3fc]/30"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Precisa de mais informações?
            </h2>
            <p className="text-white/60 mb-6">
              Fale conosco pelo WhatsApp para condições especiais, atacado e pedidos personalizados.
            </p>
            <motion.button
              onClick={() => openWhatsApp('a linha completa')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#7dd3fc] to-[#bae6fd] text-[#0a1628] font-bold rounded-xl shadow-xl shadow-[#7dd3fc]/30"
              data-testid="catalog-contact-cta"
            >
              <MessageCircle size={20} />
              <span>Falar com um consultor</span>
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
